import React from 'react';
import { Button, Result } from 'antd';
import { useTranslation } from 'react-i18next';
import LoadingBox from '../loading/LoadingBox';
import { isChunkLoadError, pageNavigation, reloadOnce, shouldReloadNow } from '../../utils/lazyWithRetry';

// Dernier filet contre la page blanche : sans ErrorBoundary, toute erreur de
// rendu (dont un ChunkLoadError après un déploiement) démonte l'application.
//   - ChunkLoadError : rechargement unique de la page (même garde anti-boucle
//     que lazyWithRetry) ;
//   - sinon, ou si la garde refuse : écran de repli avec bouton « Recharger ».

interface AppErrorFallbackProps {
    fullPage?: boolean;
}

export const AppErrorFallback: React.FC<AppErrorFallbackProps> = ({ fullPage }) => {
    // useSuspense: false : l'écran de repli doit s'afficher même si les
    // traductions ne sont pas (encore) chargées — textes par défaut en français.
    const { t } = useTranslation(undefined, { useSuspense: false });
    return (
        <div
            role="alert"
            style={fullPage
                ? { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }
                : undefined}
        >
            <Result
                status="warning"
                title={t('common.appError.title', 'Un problème est survenu')}
                subTitle={t('common.appError.description',
                    "Cette page n'a pas pu s'afficher. Une nouvelle version a peut-être été déployée : rechargez la page.")}
                extra={
                    // Action explicite de l'utilisateur : pas de garde anti-boucle.
                    <Button type="primary" onClick={() => pageNavigation.reload()}>
                        {t('common.appError.reload', 'Recharger')}
                    </Button>
                }
            />
        </div>
    );
};

interface AppErrorBoundaryProps {
    children: React.ReactNode;
    /** Écran de repli centré sur toute la hauteur (frontière racine). */
    fullPage?: boolean;
    /** Un changement de valeur (ex. id de leçon) efface l'erreur affichée. */
    resetKey?: unknown;
}

interface AppErrorBoundaryState {
    hasError: boolean;
    reloading: boolean;
}

class AppErrorBoundary extends React.Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
    state: AppErrorBoundaryState = { hasError: false, reloading: false };

    static getDerivedStateFromError(error: unknown): AppErrorBoundaryState {
        return { hasError: true, reloading: isChunkLoadError(error) && shouldReloadNow() };
    }

    componentDidCatch(error: unknown, info: React.ErrorInfo): void {
        // eslint-disable-next-line no-console
        console.error('[AppErrorBoundary]', error, info.componentStack);
        if (this.state.reloading && !reloadOnce()) {
            this.setState({ reloading: false });
        }
    }

    componentDidUpdate(prevProps: AppErrorBoundaryProps): void {
        if (this.state.hasError && !this.state.reloading && prevProps.resetKey !== this.props.resetKey) {
            this.setState({ hasError: false });
        }
    }

    render(): React.ReactNode {
        if (!this.state.hasError) return this.props.children;
        // La page se recharge : indicateur de chargement plutôt qu'un écran d'erreur.
        if (this.state.reloading) return <LoadingBox />;
        return <AppErrorFallback fullPage={this.props.fullPage} />;
    }
}

export default AppErrorBoundary;
